import { useEffect, useRef, useState } from "react";
import { Expand, Shrink } from "lucide-react";
import type { Locale, PublicEvent, PublicTenant } from "@vzlive/shared";
import { t } from "@vzlive/shared/i18n";
import { LiveChat } from "./LiveChat";
import type { ChatStreamEvent } from "../hooks/useTimeline";

type Stage = "bar" | "quarter" | "full";

interface ChatLine {
  id: string;
  author: string;
  text: string;
  at: number;
}

const DOCK_STAGE_KEY = "vzlive:dock-stage";
const RECENT_WINDOW_MS = 10 * 60 * 1000;

function readStage(): Stage {
  try {
    const value = localStorage.getItem(DOCK_STAGE_KEY);
    if (value === "quarter" || value === "full") return value;
  } catch {
    /* localStorage kann blockiert sein: Standard gilt nur im Dokument. */
  }
  return "bar";
}

/**
 * Chat-Dock: der Chat hängt unten am Ticker an und ist als Leiste
 * (nur die letzten Nachrichten eines kurzen Zeitfensters als Vorschau),
 * als Viertel- oder als Vollansicht aufklappbar. Im Dokumentfluss mit
 * position: sticky bleibt er beim Scrollen sichtbar, ohne die
 * Ticker-Meldungen zu überlagern. Auf Desktopbreite schwebt er als
 * Karte unten rechts.
 */
export function ChatDock({
  tenant,
  event,
  locale,
  onChatEvent,
  onlineNow,
}: {
  tenant: PublicTenant;
  event: PublicEvent;
  locale: Locale;
  onChatEvent: (listener: (event: ChatStreamEvent) => void) => () => void;
  onlineNow?: number;
}) {
  const [stage, setStageState] = useState<Stage>(() => readStage());
  const [lines, setLines] = useState<ChatLine[]>([]);
  const rootRef = useRef<HTMLElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const setStage = (next: Stage) => {
    setStageState(next);
    try {
      localStorage.setItem(DOCK_STAGE_KEY, next);
    } catch {
      /* Speicher blockiert: Wahl gilt nur im aktuellen Dokument. */
    }
  };

  // Auf breiten Schriften mit Platz rechts neben dem Widget/Feed löst sich
  // das Dock aus dem Fluss und hängt im Fenster-Rand, statt den
  // Ticker-Content zu überlagern.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const compute = () => {
      const desktop = window.matchMedia("(min-width: 900px)").matches;
      const rect = root.getBoundingClientRect();
      const floating = desktop && window.innerWidth - rect.right >= 440;
      root.classList.toggle("pulse-dock--float", floating);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  useEffect(() => {
    const unsubscribe = onChatEvent((change) => {
      if (change.type !== "chat.created") return;
      setLines((current) =>
        [
          ...current,
          {
            id: change.message.id,
            author: change.message.authorName,
            text: change.message.content,
            at: Date.now(),
          },
        ].slice(-8),
      );
    });
    return unsubscribe;
  }, [onChatEvent]);

  useEffect(() => {
    const prune = window.setInterval(() => {
      setLines((current) =>
        current.filter((line) => Date.now() - line.at < RECENT_WINDOW_MS),
      );
    }, 30_000);
    return () => window.clearInterval(prune);
  }, []);

  const peek = lines
    .filter((line) => Date.now() - line.at < RECENT_WINDOW_MS)
    .slice(-3);

  return (
    <aside
      ref={rootRef}
      className={`pulse-dock pulse-dock--${stage}`}
      aria-label={t(locale, "chatTitle")}
    >
      <div className="pulse-dock__head">
        <button
          type="button"
          className="pulse-dock__grab"
          aria-expanded={stage !== "bar"}
          aria-controls="pulse-dock-body"
          onClick={() => setStage(stage === "bar" ? "quarter" : "bar")}
        >
          <span className="pulse-dock__dot" aria-hidden="true" />
          <b>{t(locale, "chatTitle")}</b>
          {typeof onlineNow === "number" && (
            <span className="pulse-dock__badge">{onlineNow}</span>
          )}
          {peek.length > 0 && (
            <span className="pulse-dock__unread" aria-hidden="true">
              {peek.length} neue
            </span>
          )}
          <span className="pulse-dock__chev" aria-hidden="true">
            {stage === "bar" ? "↑" : "↓"}
          </span>
        </button>
        {stage !== "bar" && (
          <button
            type="button"
            className="pulse-dock__max"
            aria-label={
              stage === "full"
                ? t(locale, "chatShrink")
                : t(locale, "chatMaximize")
            }
            onClick={() => setStage(stage === "full" ? "quarter" : "full")}
          >
            {stage === "full" ? (
              <Shrink size={15} aria-hidden="true" />
            ) : (
              <Expand size={15} aria-hidden="true" />
            )}
          </button>
        )}
      </div>
      {stage === "bar" && (
        <div className="pulse-dock__peek" aria-hidden="true">
          {peek.map((line) => (
            <p key={line.id}>
              <b>{line.author}:</b> {line.text}
            </p>
          ))}
        </div>
      )}
      <div className="pulse-dock__scroll">
        <LiveChat
          tenant={tenant}
          event={event}
          locale={locale}
          onChatEvent={onChatEvent}
        />
      </div>
    </aside>
  );
}
